import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, Chip, Popper } from "@mui/material";
import { styled } from "@mui/material/styles";

interface ChipContainerProps {
  hasSelection?: boolean;
  isOpen?: boolean;
}

export const ChipContainer = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "hasSelection" && prop !== "isOpen",
})<ChipContainerProps>(({ theme, hasSelection, isOpen }) => ({
  borderRadius: "100px",
  paddingTop: "3px",
  paddingBottom: "3px",
  paddingLeft: "4px",
  paddingRight: "4px",
  height: "24px",
  minHeight: "24px",
  maxHeight: "24px",
  fontSize: theme.typography.body2.fontSize,
  fontWeight: hasSelection ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
  cursor: "pointer",
  transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  // Initial state (no selection): dashed border, white background
  ...(!hasSelection && {
    borderStyle: "dashed",
    backgroundColor: theme.palette.background.paper,
  }),

  // Selection state: solid border, no background
  ...(hasSelection && {
    borderStyle: "solid",
    backgroundColor: "transparent",
  }),

  // Hover state (no selection): grey background
  ...(!hasSelection && {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),

  // Hover state (with selection): keep transparent
  ...(hasSelection && {
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: theme.palette.primary.main,
    },
  }),

  // When picker is open (no selection): grey background
  ...(isOpen &&
    !hasSelection && {
      backgroundColor: theme.palette.action.hover,
    }),

  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  "&.Mui-disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
    pointerEvents: "auto",
  },

  "& .MuiChip-icon": {
    fontSize: 18,
    marginLeft: theme.spacing(0.5),
  },
}));

export const SelectionLabel = styled(Box)(({ theme }) => ({
  display: "inline",
  "& .label-text": {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },
  "& .item-text": {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    marginLeft: theme.spacing(0.5),
  },
}));

export const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
  width: "fit-content",
  minWidth: "300px",
}));

export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.paper,

  "& .MuiAutocomplete-paper": {
    marginTop: 0,
  },
  "& .MuiAutocomplete-listbox": {
    maxHeight: "320px",
  },
  "& .MuiInputBase-root": {
    paddingLeft: theme.spacing(1),
  },
})) as typeof Autocomplete;

export const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.action.active,
  fontSize: 20,
  marginRight: theme.spacing(1),
}));
